import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { ChatSocketService, ClientSurfaceEnum, CONSTANTS, Constants, ENV, Environment, LanguageService, RoleUserEnum, SocketService, User } from 'lib-trend-core';
import { ChatPushService, isSafeChatAppPath } from 'lib-trend-core/chat';
import { Observable, Subject, firstValueFrom, fromEvent, interval, merge } from 'rxjs';
import { filter, map, takeUntil, throttleTime } from 'rxjs/operators';

/** What the API answers on sign-in, refresh and handoff exchange — kept verbatim in storage. */
export interface ChatAppStoredSession {
  token: string;
  user: User;
  [key: string]: unknown;
}

/** Why a session ended — decides where the user lands and what they are told. */
export type ChatAppSessionEndReason = 'signedOut' | 'expired' | 'revoked' | 'unavailable';

/** Roles allowed to use the product — the chat app admits the same set (RF-03). */
export const CHAT_APP_ALLOWED_ROLES: ReadonlyArray<RoleUserEnum> = [
  RoleUserEnum.ADMIN,
  RoleUserEnum.SUPERVISOR,
  RoleUserEnum.ATTENDANT,
];

/** How often the token is renewed — also what re-checks a blocked user or company (RF-09). */
const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * The chat app's session runtime — SPEC-2026-09-internal-chat-pwa (T-30 · RF-08 · RF-09 ·
 * RF-10 · RF-29).
 *
 * - Signs in and exchanges handoff codes with `surface: WEB_CHAT`, so the session can
 *   never mark the user available for attendance distribution (RF-08). It never calls
 *   the attendance heartbeat.
 * - Renews the token on a fixed cadence and when the app comes back to the foreground;
 *   the socket only connects after a fresh token, and reconnects with the renewed one
 *   when the gateway refuses an expired handshake.
 * - Ends the session on revocation (the product session it came from signed out), on a
 *   refused renewal, and on sign-out. Every path unregisters this device's push token
 *   BEFORE the stored session is cleared (RF-29) — the cleanup needs the token.
 * - Ends with a hard navigation, which discards every in-memory trace of the previous
 *   session (store, timelines, drafts).
 */
@Injectable({ providedIn: 'root' })
export class ChatAppSessionService implements OnDestroy {

  private readonly destroy$ = new Subject<void>();

  private started = false;

  private ending = false;

  private lastRefreshAt = 0;

  constructor(
    private readonly http: HttpClient,
    private readonly zone: NgZone,
    private readonly socketService: SocketService,
    private readonly chatSocketService: ChatSocketService,
    private readonly chatPushService: ChatPushService,
    private readonly languageService: LanguageService,
    @Inject(CONSTANTS) private readonly constants: Constants,
    @Inject(ENV) private readonly environment: Environment,
  ) { }

  // ---------------------------------------------------------------------------
  // Stored session
  // ---------------------------------------------------------------------------

  get session(): ChatAppStoredSession | null {
    try {
      const raw = localStorage.getItem(this.constants['currentUser']);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.token ? parsed : null;
    } catch {
      return null;
    }
  }

  get user(): User | null {
    return this.session?.user ?? null;
  }

  isAuthenticated(): boolean {
    return !!this.session?.token;
  }

  /** The company has the internal chat module on. */
  hasChatModule(user: User | null = this.user): boolean {
    return user?.company?.modules?.moduleChat === true;
  }

  /** The user holds a role that may use the product. */
  hasAllowedRole(user: User | null = this.user): boolean {
    return (user?.roles ?? []).some((role) => CHAT_APP_ALLOWED_ROLES.includes(role));
  }

  private store(session: ChatAppStoredSession): void {
    localStorage.setItem(this.constants['currentUser'], JSON.stringify(session));
    this.lastRefreshAt = Date.now();
  }

  // ---------------------------------------------------------------------------
  // Entering
  // ---------------------------------------------------------------------------

  /**
   * Product credentials + human verification (RF-04). A session already stored on this
   * browser is named as the one being replaced, so the server revokes it when it belongs
   * to someone else — a shared computer never keeps the previous person's session alive.
   */
  async signIn(email: string, password: string, recaptchaToken: string): Promise<User> {
    const replacesToken = this.session?.token;
    const response = await firstValueFrom(this.http.post<ChatAppStoredSession>('/auth', {
      email,
      password,
      recaptchaToken,
      surface: ClientSurfaceEnum.WEB_CHAT,
      ...(replacesToken ? { replacesToken } : {}),
    }));
    if (!response?.token) {
      throw new Error('Sign-in answered without a session');
    }
    await this.replaceSessionIfOtherUser(response);
    this.store(response);
    return response.user;
  }

  /**
   * One-time handoff code from the product (RF-05 · RF-06). Resolves the exchanged
   * session WITHOUT storing it: the caller decides (same person → keep the current
   * session; another person or company → `adopt`).
   */
  async exchangeHandoffCode(code: string): Promise<ChatAppStoredSession> {
    const response = await firstValueFrom(this.http.post<{ data: ChatAppStoredSession }>('/auth/handoff/exchange', { code }));
    const session = response?.data;
    if (!session?.token) {
      throw new Error('Exchange answered without a session');
    }
    return session;
  }

  /** Whether an exchanged session is the same person, in the same company, as the stored one. */
  isSameIdentity(session: ChatAppStoredSession): boolean {
    const current = this.user;
    if (!current || !session?.user) return false;
    return String(current._id) === String(session.user._id)
      && String(current.company?._id ?? '') === String(session.user.company?._id ?? '');
  }

  /**
   * Makes `session` the stored one. When it belongs to someone else, this device's push
   * registration of the previous person is removed first (RF-07 · RF-29).
   */
  async adopt(session: ChatAppStoredSession): Promise<void> {
    await this.replaceSessionIfOtherUser(session);
    this.store(session);
  }

  private async replaceSessionIfOtherUser(next: ChatAppStoredSession): Promise<void> {
    if (this.session && !this.isSameIdentity(next)) {
      this.chatPushService.unregisterOnLogout();
    }
  }

  // ---------------------------------------------------------------------------
  // Running
  // ---------------------------------------------------------------------------

  /**
   * Idempotent — called by the shell once a usable session exists. Renews the token
   * first, then connects the socket with it.
   */
  start(): void {
    if (this.started || !this.isAuthenticated()) return;
    this.started = true;

    this.languageService.initFromUser(this.user ?? undefined);

    this.refresh()
      .catch(() => { /* a refused renewal already ended the session */ })
      .finally(() => {
        if (this.isAuthenticated()) {
          this.socketService.connect();
        }
      });

    interval(TOKEN_REFRESH_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh().catch(() => { /* handled in refresh */ }));

    // A phone that slept for an hour comes back with an old token: renew on return.
    merge(fromEvent(document, 'visibilitychange'), fromEvent(window, 'focus'))
      .pipe(
        filter(() => document.visibilityState === 'visible'),
        filter(() => Date.now() - this.lastRefreshAt > TOKEN_REFRESH_INTERVAL_MS),
        throttleTime(10_000),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refresh().then(() => this.socketService.reconnect()).catch(() => { /* handled */ }));

    // The gateway refused the handshake because the token expired: renew, then reconnect.
    this.socketService.connectErrors$
      .pipe(
        filter((error) => error.errorKey === 'chat.errors.sessionExpired'),
        throttleTime(5_000),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refresh().then(() => this.socketService.reconnect()).catch(() => { /* handled */ }));

    this.chatSocketService.sessionExpired$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh().then(() => this.socketService.reconnect()).catch(() => { /* handled */ }));

    // RF-10: the product session this one was opened from signed out.
    this.chatSocketService.sessionRevoked$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.zone.run(() => this.end('revoked')));
  }

  /**
   * Renews the token. A 401/403 means the session may not continue (revoked, user blocked
   * or removed, company deactivated): the session ends — to the unavailable page when the
   * module was turned off, to sign-in otherwise.
   */
  async refresh(): Promise<void> {
    if (!this.isAuthenticated()) return;
    try {
      const response = await firstValueFrom(this.http.get<ChatAppStoredSession>('/auth/refresher/'));
      if (response?.token) {
        this.store(response);
      }
    } catch (error: any) {
      const status = error?.status;
      const key = error?.error?.key;
      if (key === 'chat.errors.moduleDisabled') {
        this.end('unavailable');
      } else if (status === 401 || status === 403) {
        this.end(key === 'errors.auth.sessionRevoked' ? 'revoked' : 'expired');
      }
      throw error;
    }
  }

  // ---------------------------------------------------------------------------
  // Leaving
  // ---------------------------------------------------------------------------

  /**
   * The single exit. Order matters: push cleanup and the server sign-out both need the
   * stored token, so they are fired (keepalive — they survive the navigation) before the
   * session is cleared.
   */
  end(reason: ChatAppSessionEndReason, returnPath: string | null = this.currentPath()): void {
    if (this.ending) return;
    this.ending = true;

    this.chatPushService.unregisterOnLogout();
    if (reason === 'signedOut') {
      this.signOutOnServer();
    }
    localStorage.removeItem(this.constants['currentUser']);
    this.socketService.disconnect();
    this.clearAppBadge();

    const target = reason === 'unavailable'
      ? '/unavailable?reason=module'
      : this.signInPath(reason, reason === 'signedOut' ? null : returnPath);
    window.location.assign(target);
  }

  signInPath(reason: ChatAppSessionEndReason | null, returnPath: string | null): string {
    const params = new URLSearchParams();
    if (reason && reason !== 'signedOut') params.set('reason', reason);
    if (returnPath && returnPath !== '/' && isSafeChatAppPath(returnPath)) params.set('returnUrl', returnPath);
    const query = params.toString();
    return query ? `/login?${query}` : '/login';
  }

  /** Revokes this session's own id (best effort, survives the navigation). */
  private signOutOnServer(): void {
    const token = this.session?.token;
    if (!token) return;
    fetch(`${this.environment['apiUrl']}/auth/logout`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    }).catch(() => { /* best effort */ });
  }

  private clearAppBadge(): void {
    const nav = navigator as Navigator & { clearAppBadge?: () => Promise<void> };
    nav.clearAppBadge?.().catch(() => { /* unsupported */ });
  }

  private currentPath(): string {
    return `${window.location.pathname}${window.location.search}`;
  }

  /** Emits once the stored session is gone in another tab (sign-out elsewhere). */
  sessionClearedElsewhere$(): Observable<void> {
    return fromEvent<StorageEvent>(window, 'storage').pipe(
      filter((event) => event.key === this.constants['currentUser'] && !event.newValue),
      map(() => undefined),
      takeUntil(this.destroy$),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
